import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Frown, MessageSquare, RefreshCw } from 'lucide-react'
import { api } from '../lib/api'
import { formatDateTime } from '../lib/utils'

// Complaint-monitoring dashboard (2026-08-27 monthly-meeting roadmap
// item 2). One glance across low-star delivery ratings + open support
// tickets so ops can chase before customers churn quietly.

interface LowRating {
  id: string
  order_number: string
  rating: number
  rating_comment: string | null
  rating_at: string
  delivery_name: string | null
  delivery_phone: string | null
  total: number
  customer_name: string | null
  customer_phone: string | null
}

interface OpenTicket {
  id: string
  name: string
  email: string
  topic: string | null
  message: string
  locale: string
  source: string
  created_at: string
}

interface FeedbackResponse {
  ratings: LowRating[]
  tickets: OpenTicket[]
  window_days: number
}

export default function Feedback() {
  const [windowDays, setWindowDays] = useState(14)
  const { data, isLoading, isFetching, refetch } = useQuery<FeedbackResponse>({
    queryKey: ['admin-feedback', windowDays],
    queryFn: () => api.get(`/feedback?days=${windowDays}`).then(r => r.data),
  })

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Frown size={20} className="text-[#c41e3a]" />
            Feedback
          </h1>
          <p className="text-sm text-gray-500 mt-1 max-w-3xl">
            Low-star delivery ratings and open support tickets from the last {data?.window_days ?? windowDays} days. Chase from oldest first — a 3-star silent-departure is the most rescuable kind of unhappy customer.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={windowDays}
            onChange={e => setWindowDays(parseInt(e.target.value, 10))}
            className="bg-black/40 border border-white/10 rounded px-3 py-2 text-sm text-white"
          >
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
            <option value={60}>Last 60 days</option>
          </select>
          <button
            onClick={() => refetch()}
            className="p-2 rounded bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white"
            title="Refresh"
          >
            <RefreshCw size={16} className={isFetching ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-[#1a1a1a] rounded-xl border border-white/10 overflow-hidden">
          <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
            <Frown size={16} className="text-amber-400" />
            <h2 className="text-sm font-semibold text-white">Low ratings (≤ 3★)</h2>
            <span className="text-xs text-gray-500 ml-auto">{data?.ratings.length ?? 0} rows</span>
          </div>
          {isLoading ? (
            <div className="p-6 text-center text-sm text-gray-500">Loading…</div>
          ) : (data?.ratings ?? []).length === 0 ? (
            <div className="p-6 text-center text-sm text-gray-500">No low ratings in the window. Nice.</div>
          ) : (
            <ul className="divide-y divide-white/5">
              {data!.ratings.map(r => (
                <li key={r.id} className="p-4 hover:bg-white/[0.02]">
                  <div className="flex items-center justify-between gap-3 mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-amber-400 font-mono">{'★'.repeat(r.rating)}{'☆'.repeat(5 - r.rating)}</span>
                      <a
                        href={`/orders?search=${r.order_number}`}
                        className="text-[#c41e3a] font-semibold text-sm hover:underline"
                      >
                        {r.order_number}
                      </a>
                    </div>
                    <span className="text-[11px] text-gray-500">{formatDateTime(r.rating_at)}</span>
                  </div>
                  <div className="text-xs text-gray-400">
                    {r.customer_name ?? r.delivery_name ?? '—'} · {r.customer_phone ?? r.delivery_phone ?? '—'}
                  </div>
                  {r.rating_comment && (
                    <p className="mt-2 text-sm text-white/90 italic">&ldquo;{r.rating_comment}&rdquo;</p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-[#1a1a1a] rounded-xl border border-white/10 overflow-hidden">
          <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
            <MessageSquare size={16} className="text-blue-400" />
            <h2 className="text-sm font-semibold text-white">Open support tickets</h2>
            <span className="text-xs text-gray-500 ml-auto">{data?.tickets.length ?? 0} rows</span>
          </div>
          {isLoading ? (
            <div className="p-6 text-center text-sm text-gray-500">Loading…</div>
          ) : (data?.tickets ?? []).length === 0 ? (
            <div className="p-6 text-center text-sm text-gray-500">No open tickets. Nice.</div>
          ) : (
            <ul className="divide-y divide-white/5">
              {data!.tickets.map(t => (
                <li key={t.id} className="p-4 hover:bg-white/[0.02]">
                  <div className="flex items-center justify-between gap-3 mb-1">
                    <div className="text-sm text-white font-semibold">
                      {t.name} <span className="text-gray-500 font-normal">· {t.email}</span>
                    </div>
                    <span className="text-[11px] text-gray-500">{formatDateTime(t.created_at)}</span>
                  </div>
                  {t.topic && <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">{t.topic}</div>}
                  <p className="text-sm text-white/90 line-clamp-3">{t.message}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="text-xs text-gray-500 max-w-3xl">
        <strong className="text-gray-400">How this feeds ops.</strong> Every rating ≤ 3★ also fires a <code className="text-white/80">low_rating</code> notification through Notification routing — configure who should receive them there. Open support tickets don&apos;t auto-notify from this page; they use the existing <code className="text-white/80">new_support_ticket</code> topic.
      </div>
    </div>
  )
}
